import {Injectable, OnDestroy} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, of, Subject, Subscription} from "rxjs";
import {catchError, debounceTime, distinctUntilChanged, switchMap, tap} from "rxjs/operators";
import {
    DEFAULT_REAL_TIME_COUNT_CONFIG,
    RealTimeCountConfig,
    RealTimeCountRequest,
    RealTimeCountResponse,
    RealTimeCountTriggerType
} from "../../model/dto/api/real-time-count.interface";
import {SelectionCriteriaTree} from "../../model/dto/api/selection-criteria-tree.interface";

/**
 * Service for real-time member count estimation.
 * Implements F-025: Real-Time Member Count Updates.
 *
 * Provides live updating of estimated member counts as selection criteria
 * are being built, showing the impact of each criterion addition on the
 * total count without requiring explicit preview.
 */
@Injectable({
    providedIn: 'root'
})
export class RealTimeCountService extends MvsCrudService implements OnDestroy {

    private countUpdateSubject = new Subject<RealTimeCountRequest>();
    private currentCountSubject = new BehaviorSubject<RealTimeCountResponse | null>(null);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private config: RealTimeCountConfig = {...DEFAULT_REAL_TIME_COUNT_CONFIG};
    private debouncedSubscription: Subscription | null = null;

    public currentCount$ = this.currentCountSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/realTimeCount');
        this.setupDebouncedUpdates();
    }

    ngOnDestroy(): void {
        this.debouncedSubscription?.unsubscribe();
        this.countUpdateSubject.complete();
        this.currentCountSubject.complete();
        this.loadingSubject.complete();
    }

    getObjectIcon(): string {
        return 'fa-solid fa-calculator';
    }

    /**
     * Configures the real-time count service.
     */
    configure(config: Partial<RealTimeCountConfig>): void {
        this.config = {...this.config, ...config};
    }

    /**
     * Gets the current configuration.
     */
    getConfig(): RealTimeCountConfig {
        return {...this.config};
    }

    /**
     * Estimates member count for the given criteria.
     * Returns immediately with the count result.
     */
    estimateCount(request: RealTimeCountRequest): Observable<RealTimeCountResponse> {
        return this.postGeneric<RealTimeCountResponse>(request, '/estimate');
    }

    /**
     * Estimates member count for a specific selection criteria.
     */
    estimateCountForCriteria(
        criteriaId: number,
        useCache: boolean = true,
        triggerType?: RealTimeCountTriggerType
    ): Observable<RealTimeCountResponse> {
        const params = new URLSearchParams();
        params.set('useCache', String(useCache));
        if (triggerType) {
            params.set('triggerType', triggerType);
        }
        return this.getGeneric<RealTimeCountResponse>(`/criteria/${criteriaId}?${params.toString()}`);
    }

    /**
     * Gets the latest completed estimate for a selection criteria.
     */
    getLatestEstimate(criteriaId: number): Observable<RealTimeCountResponse> {
        return this.getGeneric<RealTimeCountResponse>(`/criteria/${criteriaId}/latest`);
    }

    /**
     * Cancels any pending estimates for a selection criteria.
     */
    cancelPendingEstimates(criteriaId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/criteria/${criteriaId}/pending`);
    }

    /**
     * Estimates count for a campaign's selection criteria.
     */
    estimateCountForCampaign(campaignId: number, useCache: boolean = true): Observable<RealTimeCountResponse> {
        return this.getGeneric<RealTimeCountResponse>(`/campaign/${campaignId}?useCache=${useCache}`);
    }

    /**
     * Triggers a debounced count update.
     * Use this when criteria are being modified rapidly (e.g., user typing).
     */
    triggerDebouncedUpdate(request: RealTimeCountRequest): void {
        if (!this.config.enabled) return;
        request.isDebounced = true;
        this.countUpdateSubject.next(request);
    }

    /**
     * Triggers an immediate count update.
     * Use this for discrete actions like adding/removing filters.
     */
    triggerImmediateUpdate(request: RealTimeCountRequest): void {
        if (!this.config.enabled) return;

        this.loadingSubject.next(true);
        this.estimateCount(request).subscribe({
            next: (response) => {
                this.currentCountSubject.next(response);
                this.loadingSubject.next(false);
            },
            error: (error) => {
                console.error('Error estimating count:', error);
                this.loadingSubject.next(false);
            }
        });
    }

    /**
     * Updates count when criteria tree changes.
     */
    onCriteriaChange(
        criteriaId: number,
        criteriaTree: SelectionCriteriaTree,
        triggerType: RealTimeCountTriggerType
    ): void {
        if (!this.config.autoRefreshOnChange) return;

        const request: RealTimeCountRequest = {
            selectionCriteriaId: criteriaId,
            criteriaTree: criteriaTree,
            useCache: this.config.cacheEnabled,
            triggerType: triggerType
        };

        this.triggerDebouncedUpdate(request);
    }

    /**
     * Gets the current count value.
     */
    getCurrentCount(): RealTimeCountResponse | null {
        return this.currentCountSubject.getValue();
    }

    /**
     * Clears the current count.
     */
    clearCurrentCount(): void {
        this.currentCountSubject.next(null);
    }

    /**
     * Sets up the debounced update stream with proper error handling.
     */
    private setupDebouncedUpdates(): void {
        this.debouncedSubscription = this.countUpdateSubject.pipe(
            debounceTime(this.config.debounceMs),
            distinctUntilChanged((prev, curr) =>
                JSON.stringify(prev.criteriaTree) === JSON.stringify(curr.criteriaTree)
            ),
            tap(() => this.loadingSubject.next(true)),
            switchMap(request => this.estimateCount(request).pipe(
                catchError(error => {
                    console.error('Error estimating count:', error);
                    return of(null);
                })
            ))
        ).subscribe({
            next: (response) => {
                if (response) {
                    this.currentCountSubject.next(response);
                }
                this.loadingSubject.next(false);
            },
            error: (error) => {
                console.error('Error in debounced count update:', error);
                this.loadingSubject.next(false);
            }
        });
    }

    /**
     * Formats the count for display (with thousands separators).
     */
    formatCount(count: number | null | undefined): string {
        if (count === null || count === undefined) {
            return '-';
        }
        return count.toLocaleString();
    }

    /**
     * Formats the delta for display (with +/- prefix).
     */
    formatDelta(delta: number | null | undefined): string {
        if (delta === null || delta === undefined) {
            return '';
        }
        if (delta > 0) {
            return `+${delta.toLocaleString()}`;
        }
        return delta.toLocaleString();
    }

    /**
     * Gets the CSS class for the delta indicator.
     */
    getDeltaClass(delta: number | null | undefined): string {
        if (delta === null || delta === undefined) {
            return 'text-gray-400';
        }
        if (delta > 0) {
            return 'text-green-600';
        }
        if (delta < 0) {
            return 'text-red-600';
        }
        return 'text-gray-500';
    }

    /**
     * Gets the icon for the delta indicator.
     */
    getDeltaIcon(delta: number | null | undefined): string {
        if (delta === null || delta === undefined) {
            return 'fa-solid fa-question';
        }
        if (delta > 0) {
            return 'fa-solid fa-arrow-up';
        }
        if (delta < 0) {
            return 'fa-solid fa-arrow-down';
        }
        return 'fa-solid fa-minus';
    }

    /**
     * Gets the CSS class for the performance indicator.
     */
    getPerformanceClass(executionTimeMs: number | null | undefined): string {
        if (executionTimeMs === null || executionTimeMs === undefined) {
            return '';
        }
        if (executionTimeMs < 500) {
            return 'text-green-600';
        }
        if (executionTimeMs < 2000) {
            return 'text-blue-600';
        }
        if (executionTimeMs < 5000) {
            return 'text-yellow-600';
        }
        return 'text-red-600';
    }
}
